import './leaflet.css';
import './LeafletMap.scss';

import React, {
  FunctionComponent,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as L from 'leaflet';
import 'leaflet.heat';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';

import { CollapseCard, IconButton, PendingContent, Spinner } from 'plume-ui';
import { CollapseCardStyles } from 'plume-ui/dist/components/Card/CollapseCard';

import { textColorForBackground } from '../../../../utils/textUtils';
import { IssueMapMarker } from '../../types';
import CustomZoomControl from './renderers/CustomZoomControl';
import { LeafletMapMarkerGroup } from './types';

const DEFAULT_ZOOM = 4;
const START_LOAD_DELAY = 1000;

export type LeafletMapProps = {
  markerGroups: LeafletMapMarkerGroup[];
  /** Number of expected markers (for proper loading states). */
  markerCount: number;
  lat?: number;
  long?: number;
  noZoom?: boolean;
  zoom?: number;
  label?: string;
  limit?: number;
  zoomControl?: boolean;
  dragging?: boolean;
  height?: number;
  isLoading?: boolean;
  showMarkersAtZoom?: number | number[];
  showHeatmapAtZoom?: number | number[];
  heatmapIntensity?: number;
  onMarkerClick?: (issueMapMarker: IssueMapMarker) => void;
  /** Whether to wrap the map in a CollapseCard. Defaults to true. */
  withCard?: boolean;
};

const RenderCircleMarkers = ({
  markerData,
  mapRef,
  onFinishLoad,
  zoom,
  showMarkersAtZoom,
}: {
  markerData: LeafletMapMarkerGroup[];
  mapRef: React.RefObject<L.Map>;
  onFinishLoad?: () => void;
  zoom: number;
  showMarkersAtZoom?: number | number[];
}) => {
  const markersLayer = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (mapRef.current) {
      markersLayer.current = L.layerGroup([], { pane: 'clusters' }).addTo(
        mapRef.current,
      );
      if (!mapRef.current.getPane('clusters'))
        mapRef.current.createPane('clusters');
      const pane = mapRef.current.getPane('clusters');
      if (pane) {
        pane.style.zIndex = '500';
        pane.style.transition = 'opacity 0.3s ease-in-out';
        if (showMarkersAtZoom) {
          const shouldShow = Array.isArray(showMarkersAtZoom)
            ? zoom >= showMarkersAtZoom[0] && zoom <= showMarkersAtZoom[1]
            : zoom >= showMarkersAtZoom;
          if (!shouldShow) {
            pane.style.opacity = '0';
          }
        }
      }

      // Check if there are any markers to render
      const totalMarkers = markerData.reduce(
        (sum, group) => sum + group.markers.length,
        0,
      );

      if (totalMarkers === 0) {
        // No markers to render, call onFinishLoad immediately
        setTimeout(() => {
          onFinishLoad?.();
        }, START_LOAD_DELAY);
        return;
      }

      setTimeout(() => {
        markerData.forEach((markerGroup) => {
          const color = markerGroup.color || '#6269FF';
          const markerCluster = L.markerClusterGroup({
            maxClusterRadius: 140, // larger radius to have less clusters (performance)
            animate: true,
            chunkedLoading: true,
            chunkInterval: 500,
            chunkDelay: 500,
            clusterPane: 'clusters',
            polygonOptions: {
              color: color,
            },
            chunkProgress(processedMarkers, totalMarkers) {
              if (processedMarkers === totalMarkers) {
                onFinishLoad?.();
              }
            },
            spiderfyDistanceMultiplier: 2,
            removeOutsideVisibleBounds: true,
            iconCreateFunction: (cluster) => {
              const count = cluster.getChildCount();
              return L.divIcon({
                html: `<div class="marker-cluster" style="background-color: ${color}44; 
              width: ${_.clamp(count / 10, 25, 50)}px;
              height: ${_.clamp(count / 10, 25, 50)}px;
              ">
              <div style="background-color: ${color};">
              <span style="color: ${textColorForBackground(
                color,
              )};">${count}<span/></div></div>`,
              });
            },
          });

          // optimization for large number of markers
          // will not render markers outside of viewport
          // https://github.com/Leaflet/Leaflet.markercluster/issues/278
          (markerCluster as any)._getExpandedVisibleBounds = function () {
            return (markerCluster as any)._map.getBounds();
          };

          markerCluster.addTo(markersLayer.current!);

          const markerLayers = markerGroup.markers.map((marker) => {
            const leafletMarker = L.circleMarker([marker.lat, marker.long], {
              radius: 5,
              color: color,
              fillOpacity: 1,
              pane: 'clusters',
            }).on('click', () => {
              marker.onClick?.();
            });

            if (marker.tooltip)
              leafletMarker.bindTooltip(marker.tooltip, {
                direction: 'auto',
                offset: [0, 0],
                opacity: 1,
              });

            return leafletMarker;
          });
          markerCluster.addLayers(markerLayers);
        });
      }, START_LOAD_DELAY);

      return () => {
        markersLayer.current?.clearLayers();
        markersLayer.current?.remove();
      };
    }
  }, [markerData]);

  useEffect(() => {
    if (!mapRef.current) return;
    const pane = mapRef.current.getPane('clusters');
    if (showMarkersAtZoom && pane) {
      if (Array.isArray(showMarkersAtZoom)) {
        if (showMarkersAtZoom[0] > zoom && zoom > (showMarkersAtZoom[1] || 0)) {
          pane.style.opacity = '0';
          pane.style.pointerEvents = 'none';
        } else {
          pane.style.opacity = '1';
          pane.style.pointerEvents = 'auto';
        }
      } else if (zoom >= showMarkersAtZoom) {
        pane.style.opacity = '1';
        pane.style.pointerEvents = 'auto';
      } else {
        pane.style.opacity = '0';
        pane.style.pointerEvents = 'none';
      }
    }
  }, [zoom]);

  return null;
};

const RenderHeatMap = ({
  markerData,
  heatmapIntensity = 70,
}: {
  markerData: LeafletMapMarkerGroup[];
  heatmapIntensity?: number;
}) => {
  const map = useMap();
  const heatmap = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    heatmap.current = L.layerGroup().addTo(map);
    markerData.forEach((markerGroup) => {
      if (markerGroup.heatmap) {
        const heatMapData: Array<L.HeatLatLngTuple> = markerGroup.markers.map(
          (marker) => [marker.lat, marker.long, heatmapIntensity],
        );
        const heatMap = L.heatLayer(heatMapData, {
          gradient: {
            0: '#656cff', // Purple
            0.5: '#fa1478', // Red
            1: '#ffc500', // Yellow
          },
          radius: 28,
          blur: 20,
          minOpacity: 0.3,
          ...markerGroup.heatmapConfig,
        });

        heatMap.addTo(heatmap.current!);
      }
    });

    return () => {
      heatmap.current?.clearLayers();
      heatmap.current?.remove();
    };
  }, [markerData]);

  return null;
};

/**  FitBounds component is used to fit the map to the bounds of the markers.
 The CollapseCard does not render children when closed, so we otherwise
 would not have access to the mapRef to fit the bounds when opening/closing
*/
const FitBounds = ({
  markerGroups,
}: {
  markerGroups: LeafletMapMarkerGroup[];
}) => {
  const map = useMap();

  useEffect(() => {
    if (markerGroups && markerGroups.length > 0) {
      const bounds = L.latLngBounds(
        markerGroups
          .flatMap((group) => group.markers)
          .map((item) => [item.lat, item.long]),
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  }, [markerGroups, map]);

  return null;
};

const LeafletMap: FunctionComponent<LeafletMapProps> = ({
  zoom = DEFAULT_ZOOM,
  lat = 27.6462,
  long = -81.9227,
  limit = 200_000,
  height = 500,
  dragging = true,
  markerGroups,
  markerCount,
  isLoading = false,
  showMarkersAtZoom,
  heatmapIntensity,
  showHeatmapAtZoom,
  withCard = true,
}) => {
  const isAboveLimit = () => markerCount > limit;

  const [cardOpen, setCardOpen] = useState<boolean>(!isAboveLimit());
  const [mapLoading, setMapLoading] = useState<boolean>(true);

  const [currentZoom, setCurrentZoom] = useState<number>(zoom);

  const { t } = useTranslation();
  const mapRef = useRef<L.Map>(null);

  const openCloseCard = () => {
    setCardOpen((prev) => !prev);
    setMapLoading(true);
  };

  const handleZoom = _.throttle((zoom) => {
    setCurrentZoom(zoom);
  }, 50);

  const mapContent = (
    <PendingContent loading={mapLoading || isLoading} loader={Spinner}>
      <MapContainer
        preferCanvas={true}
        renderer={L.canvas()}
        center={[lat, long]}
        zoom={zoom}
        maxBounds={[
          [-90, -180],
          [90, 180],
        ]}
        ref={(node) => {
          if (node) {
            (mapRef.current as any) = node;

            node.on('zoom', () => {
              handleZoom(node.getZoom());
            });
          }
        }}
        minZoom={2}
        maxZoom={16}
        zoomControl={false}
        dragging={dragging}
        doubleClickZoom={false}
        scrollWheelZoom={false}
        style={{ height: height || '360px', width: '100%' }}
        attributionControl={false}
        className="mapZoom-container"
        markerZoomAnimation
      >
        <CustomZoomControl />
        <TileLayer url="https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png" />
        <RenderCircleMarkers
          mapRef={mapRef}
          markerData={markerGroups}
          onFinishLoad={() => {
            setMapLoading(false);
          }}
          zoom={currentZoom}
          showMarkersAtZoom={showMarkersAtZoom}
        />
        {showHeatmapAtZoom &&
          (Array.isArray(showHeatmapAtZoom)
            ? currentZoom >= showHeatmapAtZoom[0] &&
              currentZoom <= showHeatmapAtZoom[1]
            : currentZoom >= showHeatmapAtZoom) && (
            <RenderHeatMap
              heatmapIntensity={heatmapIntensity}
              markerData={markerGroups}
            />
          )}
        <FitBounds markerGroups={markerGroups} />
      </MapContainer>
    </PendingContent>
  );

  if (!withCard) {
    return <div className="LeafletMap">{mapContent}</div>;
  }

  return (
    <div className="LeafletMap">
      <CollapseCard
        title={
          <div className="LeafletMap__collapse-card__title">
            {t('monitors.monitorInfo.map.title')}
          </div>
        }
        subtitle={
          isAboveLimit() ? t('monitors.monitorInfo.map.subtitle') : undefined
        }
        classes={(current: CollapseCardStyles) => ({
          ...current,
          root: `${current.root} LeafletMap__collapse-card`,
          content: `${current.content} LeafletMap__collapse-card__content`,
        })}
        activeHeader
        open={cardOpen}
        actions={[<IconButton onClick={openCloseCard}></IconButton>]}
      >
        {mapContent}
      </CollapseCard>
    </div>
  );
};

export default memo(LeafletMap);
